/**
 * ArchiveRecordDetail — the archive cover sheet + lifecycle actions
 * (drafts/57 ARC-02 approval + ARC-04 legal-hold / destruction).
 *   - PENDING_APPROVAL → Approve / Reject (risk-based approver shown).
 *   - Legal hold place / lift (a held record can't be destroyed).
 *   - ACTIVE (not held) → Schedule destruction.
 * Route: /archive/records/$id.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, CardBody, CardHeader, useToast } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import { STATUS_STYLE, RISK_STYLE, fmtDate, type ArchiveRecord } from './types';
import head from '../Recalls/Recalls.module.css';

const cardBtn = (bg: string, disabled = false): React.CSSProperties => ({
  padding: '8px 16px', borderRadius: 8, border: 'none', background: disabled ? '#9CA3AF' : bg,
  color: 'white', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14,
});

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ color: '#6B7989', fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 14 }}>{children}</span>
    </div>
  );
}

export function ArchiveRecordDetail({ recordId }: { recordId: string }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [rejectReason, setRejectReason] = useState('');
  const [holdRef, setHoldRef] = useState('');
  const [showReject, setShowReject] = useState(false);

  const key = ['archive', 'record', recordId];
  const q = useQuery<ArchiveRecord>({
    queryKey: key,
    queryFn: ({ signal }) => apiFetch<ArchiveRecord>(`/api/v1/archive/records/${recordId}`, { signal }),
  });

  const refresh = (rec: ArchiveRecord) => {
    qc.setQueryData(key, rec);
    qc.invalidateQueries({ queryKey: ['archive'] });
  };
  const onErr = (e: unknown, fallback: string) => toast.error(fallback, e instanceof Error ? e.message : undefined);

  const approve = useMutation({
    mutationFn: (decision: 'APPROVE' | 'REJECT') => apiFetch<ArchiveRecord>(`/api/v1/archive/records/${recordId}/approve`, {
      method: 'POST', body: decision === 'REJECT' ? { decision, rejection_reason: rejectReason.trim() } : { decision },
    }),
    onSuccess: (rec, decision) => { refresh(rec); setShowReject(false); toast.success(decision === 'APPROVE' ? t('archive.detail.approved', { defaultValue: 'Record approved — now Active.' }) : t('archive.detail.rejected', { defaultValue: 'Record rejected.' })); },
    onError: (e) => onErr(e, t('archive.detail.approveFailed', { defaultValue: 'Approval action failed' })),
  });
  const placeHold = useMutation({
    mutationFn: () => apiFetch<ArchiveRecord>(`/api/v1/archive/records/${recordId}/legal-hold`, { method: 'POST', body: { legal_hold_ref: holdRef.trim() } }),
    onSuccess: (rec) => { refresh(rec); setHoldRef(''); toast.success(t('archive.detail.held', { defaultValue: 'Legal hold placed.' })); },
    onError: (e) => onErr(e, t('archive.detail.holdFailed', { defaultValue: 'Could not place legal hold' })),
  });
  const liftHold = useMutation({
    mutationFn: () => apiFetch<ArchiveRecord>(`/api/v1/archive/records/${recordId}/legal-hold`, { method: 'DELETE' }),
    onSuccess: (rec) => { refresh(rec); toast.success(t('archive.detail.lifted', { defaultValue: 'Legal hold lifted.' })); },
    onError: (e) => onErr(e, t('archive.detail.liftFailed', { defaultValue: 'Could not lift legal hold' })),
  });
  const schedule = useMutation({
    mutationFn: () => apiFetch<ArchiveRecord>(`/api/v1/archive/records/${recordId}/schedule-destruction`, { method: 'POST', body: {} }),
    onSuccess: (rec) => { refresh(rec); toast.success(t('archive.detail.scheduled', { defaultValue: 'Queued for destruction (dual-signature disposition follows).' })); },
    onError: (e) => onErr(e, t('archive.detail.scheduleFailed', { defaultValue: 'Could not schedule destruction' })),
  });

  if (q.isLoading) return <div style={{ padding: 24 }} role="status">{t('common.loading', { defaultValue: 'Loading…' })}</div>;
  if (q.isError || !q.data) return <div style={{ padding: 24, color: '#B91C1C' }} role="alert">{t('archive.detail.error', { defaultValue: 'Could not load this record.' })}</div>;

  const r = q.data;
  const s = STATUS_STYLE[r.status];
  const busy = approve.isPending || placeHold.isPending || liftHold.isPending || schedule.isPending;

  return (
    <div style={{ padding: 'var(--space-6, 24px)', display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1000 }}>
      <nav className={head.crumbs} aria-label="Breadcrumb">
        <Link to="/archive" className={head.crumb}>{t('archive.dashboard.title', { defaultValue: 'Archive & Records' })}</Link>
        <span className={head.crumbSep} aria-hidden>/</span>
        <Link to="/archive/records" className={head.crumb}>{t('archive.records.title', { defaultValue: 'Archive Records' })}</Link>
        <span className={head.crumbSep} aria-hidden>/</span>
        <span>{r.title}</span>
      </nav>

      <header className={head.detailHead}>
        <div>
          <h1 className={head.detailTitle}>{r.title}</h1>
          <div className={head.subline}>
            <span style={{ background: s.bg, color: s.fg, borderRadius: 999, padding: '3px 12px', fontSize: 13, fontWeight: 700 }}>{s.label}</span>
            <span style={{ background: RISK_STYLE[r.risk_class].bg, color: RISK_STYLE[r.risk_class].fg, borderRadius: 999, padding: '3px 12px', fontSize: 13, fontWeight: 600 }}>{r.risk_class} risk</span>
            {r.legal_hold_active && <span style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 999, padding: '3px 12px', fontSize: 13, fontWeight: 700 }}>🔒 {t('archive.detail.onHold', { defaultValue: 'Legal hold' })}</span>}
          </div>
        </div>
      </header>

      <Card>
        <CardHeader><h2 style={{ margin: 0, fontSize: 16 }}>{t('archive.detail.coverSheet', { defaultValue: 'Cover sheet' })}</h2></CardHeader>
        <CardBody>
          <Row label={t('archive.detail.type', { defaultValue: 'Record type' })}>{r.record_type}</Row>
          <Row label={t('archive.detail.document', { defaultValue: 'Source document' })}>{r.document_number ?? '—'}</Row>
          <Row label={t('archive.detail.dept', { defaultValue: 'Department' })}>{r.department_name ?? '—'}</Row>
          <Row label={t('archive.detail.site', { defaultValue: 'Site' })}>{r.site_name ?? '—'}</Row>
          <Row label={t('archive.detail.owner', { defaultValue: 'Owner' })}>{r.owner_name ?? '—'}</Row>
          <Row label={t('archive.detail.archivedDate', { defaultValue: 'Archived date' })}>{fmtDate(r.archived_date, i18n.language)}</Row>
          <Row label={t('archive.detail.confidentiality', { defaultValue: 'Confidentiality' })}>{r.confidentiality}</Row>
          <Row label={t('archive.detail.storage', { defaultValue: 'Storage' })}>{r.storage_medium}{r.storage_location ? ` — ${r.storage_location}` : ''}</Row>
          <Row label={t('archive.detail.source', { defaultValue: 'Source system' })}>{r.source_system ?? '—'}</Row>
          <Row label={t('archive.detail.retention', { defaultValue: 'Retention' })}>
            {r.retention_policy_name ?? '—'}{r.retention_end_at ? ` · ends ${fmtDate(r.retention_end_at, i18n.language)}` : ''}
          </Row>
          <Row label={t('archive.detail.reason', { defaultValue: 'Archive reason' })}>{r.archive_reason ?? '—'}</Row>
          <Row label={t('archive.detail.requiredApprover', { defaultValue: 'Required approver' })}><strong>{r.required_approver_role}</strong></Row>
          {r.approved_by_name && <Row label={t('archive.detail.approvedBy', { defaultValue: 'Approved by' })}>{r.approved_by_name} · {fmtDate(r.approved_at_utc, i18n.language)}</Row>}
          {r.rejection_reason && <Row label={t('archive.detail.rejectionReason', { defaultValue: 'Rejection reason' })}>{r.rejection_reason}</Row>}
          {r.legal_hold_active && <Row label={t('archive.detail.holdRef', { defaultValue: 'Legal hold ref' })}>{r.legal_hold_ref ?? '—'}</Row>}
          {r.destruction_scheduled_at && <Row label={t('archive.detail.destructionScheduled', { defaultValue: 'Destruction scheduled' })}>{fmtDate(r.destruction_scheduled_at, i18n.language)}</Row>}
          {r.destroyed_at_utc && <Row label={t('archive.detail.destroyed', { defaultValue: 'Destroyed' })}>{fmtDate(r.destroyed_at_utc, i18n.language)}</Row>}
          {r.content_hash_at_destroy && <Row label={t('archive.detail.hash', { defaultValue: 'Content hash (preserved)' })}><code style={{ fontSize: 12, wordBreak: 'break-all' }}>{r.content_hash_at_destroy}</code></Row>}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h2 style={{ margin: 0, fontSize: 16 }}>{t('archive.detail.actions', { defaultValue: 'Actions' })}</h2></CardHeader>
        <CardBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Approval (risk-based gate) */}
            {r.status === 'PENDING_APPROVAL' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 14, color: '#475569' }}>
                  {t('archive.detail.approvalPrompt', { defaultValue: 'This record requires {{role}} approval before it becomes an active archive record.', role: r.required_approver_role })}
                </div>
                {!showReject ? (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" disabled={busy} onClick={() => approve.mutate('APPROVE')} style={cardBtn('#0E7C5A', busy)}>{t('archive.detail.approve', { defaultValue: 'Approve' })}</button>
                    <button type="button" disabled={busy} onClick={() => setShowReject(true)} style={cardBtn('#B91C1C', busy)}>{t('archive.detail.reject', { defaultValue: 'Reject' })}</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t('archive.detail.rejectPlaceholder', { defaultValue: 'Reason for rejection (required)' })} style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db', minHeight: 60 }} />
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="button" disabled={busy || rejectReason.trim().length < 5} onClick={() => approve.mutate('REJECT')} style={cardBtn('#B91C1C', busy || rejectReason.trim().length < 5)}>{t('archive.detail.confirmReject', { defaultValue: 'Confirm rejection' })}</button>
                      <button type="button" onClick={() => { setShowReject(false); setRejectReason(''); }} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 600 }}>{t('common.cancel', { defaultValue: 'Cancel' })}</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Legal hold */}
            {r.status !== 'DESTROYED' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{t('archive.detail.legalHold', { defaultValue: 'Legal hold' })}</div>
                {r.legal_hold_active ? (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ color: '#6B7989', fontSize: 13 }}>{t('archive.detail.heldUnder', { defaultValue: 'Held under' })}: <strong>{r.legal_hold_ref}</strong></span>
                    <button type="button" disabled={busy} onClick={() => liftHold.mutate()} style={cardBtn('#475569', busy)}>{t('archive.detail.liftHold', { defaultValue: 'Lift hold' })}</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input value={holdRef} onChange={(e) => setHoldRef(e.target.value)} placeholder={t('archive.detail.holdRefPlaceholder', { defaultValue: 'Case / matter reference' })} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', minWidth: 240 }} />
                    <button type="button" disabled={busy || holdRef.trim().length < 2} onClick={() => placeHold.mutate()} style={cardBtn('#B45309', busy || holdRef.trim().length < 2)}>{t('archive.detail.placeHold', { defaultValue: 'Place legal hold' })}</button>
                  </div>
                )}
              </div>
            )}

            {/* Destruction scheduling */}
            {(r.status === 'ACTIVE' || r.status === 'PENDING_DESTRUCTION') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{t('archive.detail.destruction', { defaultValue: 'Destruction' })}</div>
                {r.legal_hold_active ? (
                  <div style={{ color: '#B91C1C', fontSize: 13 }}>{t('archive.detail.cannotDestroyHold', { defaultValue: 'Cannot schedule destruction while on legal hold. Lift the hold first.' })}</div>
                ) : r.status === 'PENDING_DESTRUCTION' ? (
                  <div style={{ color: '#9A3412', fontSize: 13 }}>{t('archive.detail.alreadyQueued', { defaultValue: 'Already queued for destruction. Final destroy requires dual-signature disposition.' })}</div>
                ) : (
                  <div>
                    <button type="button" disabled={busy} onClick={() => schedule.mutate()} style={cardBtn('#9A3412', busy)}>{t('archive.detail.scheduleDestruction', { defaultValue: 'Schedule destruction' })}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
